import { IWithWorksheetInjectedProps } from 'Components/Worksheets/Core';
import { hasEmptyValue } from 'Lib/Any';
import { pipe } from 'Lib/Hooks/Selectors';
import { IconSyncProblem } from 'Lib/Icons';
import React, { useState } from 'react';
import { IconBaseProps } from 'react-icons';
import { shallowEqual, useSelector } from 'react-redux';
import { Button, Dialog, FieldLabelText, Link } from '../../../Core';
import { withErrorBoundary } from '../../../Core/ErrorBoundary';
import { withInjectedProps } from '../../../Core/InjectedPropsContext';

type WorksheetBatchUpdateUpdateFailureIconProps = IWithWorksheetInjectedProps & {
  title?: string;
  iconSize?: IconBaseProps['size'];
};

/**
 * Icon that indicates a worksheet was not updated as expected after the last update
 *
 * When clicked, a dialog popup lists the fields that could not be updated
 */
let WorksheetBatchUpdateUpdateFailureIcon: React.FC<WorksheetBatchUpdateUpdateFailureIconProps> = ({
  title,
  iconSize = '1.5em',
  createWorksheetSelector,
  worksheetId,
}) => {
  const [showDialog, setShowDialog] = useState(false);

  const updateFailureFields = useSelector(
    (state) => Object.keys(state.worksheetBatchUpdate.updateFailures[worksheetId!] ?? {}),
    shallowEqual
  );

  const worksheetLinkText = useSelector(
    pipe(
      createWorksheetSelector!(''),
      (w) => `${w.header?.Sample?.SampleId} ${w.header?.TopLevelTest?.WorksheetType?.ShortName ?? w.type?.ShortName}`
    )
  );

  if (hasEmptyValue(updateFailureFields)) return null;

  return (
    <>
      <Button warning subtle noPadding alwaysEnabled onClick={() => setShowDialog(true)}>
        <IconSyncProblem size={iconSize} />
      </Button>
      <Dialog
        title={title ?? `Could not apply all fields to worksheet`}
        width="500px"
        visible={showDialog}
        onClose={() => setShowDialog(false)}
        buttons={[
          <Button key="0" alwaysEnabled onClick={() => setShowDialog(false)}>
            Close
          </Button>,
        ]}
      >
        <p>
          The following fields could not be set to the entered value on the worksheet{' '}
          {
            <Link to={`/worksheets/${worksheetId}`} openInNewTab>
              {worksheetLinkText}
            </Link>
          }
          :
        </p>
        <ul>
          {updateFailureFields.map((k, i) => (
            <li key={i}>
              <FieldLabelText name={k} />
            </li>
          ))}
        </ul>
        <br />
        <p>Please review this worksheet.</p>
      </Dialog>
    </>
  );
};

WorksheetBatchUpdateUpdateFailureIcon = withErrorBoundary(withInjectedProps(WorksheetBatchUpdateUpdateFailureIcon));

export { WorksheetBatchUpdateUpdateFailureIcon };
export default WorksheetBatchUpdateUpdateFailureIcon;
